import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_MODEL_AVATAR } from '@/app/[locale]/(app)/project/constants';
import { BaseLineCheck2 } from '@/app/components/icons/BaseLineCheck2';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { Button } from '@/app/components/ui/button';
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from '@/app/components/ui/hover-card';
import { Textarea } from '@/app/components/ui/textarea';
import { cn } from '@/app/utils';

export const AgentModelNodeView = ({ node, updateAttributes }: NodeViewProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(node.attrs.description || '');
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations();

  // 进入编辑模式
  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditing(true);
    setEditValue(node.attrs.description);
  };

  // 自动聚焦输入框
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // 保存编辑
  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== node.attrs.description) {
      updateAttributes({
        description: trimmedValue,
      });
    }
    setIsEditing(false);
  };

  // 显示的内容
  const displayText = node.attrs.description;

  const isDescriptionChanged = useMemo(() => {
    return node.attrs.description !== node.attrs.originalDescription;
  }, [node.attrs.description, node.attrs.originalDescription]);

  return (
    <NodeViewWrapper
      as="span"
      data-drag-handle
      className={cn(
        'agent-model relative mx-1 inline-flex h-[22px] items-center gap-1 rounded-8 border border-system-white24 p-[1px] align-middle text-xs leading-[1.4]',
        isEditing ? 'cursor-text' : 'cursor-grab',
      )}
      // 阻止编辑状态下的拖拽
      draggable={!isEditing}
    >
      <img
        className="size-4 flex-shrink-0 rounded-full object-cover"
        src={isDescriptionChanged ? DEFAULT_MODEL_AVATAR : node.attrs.avatar || DEFAULT_MODEL_AVATAR}
        alt={node.attrs.name}
      />

      <HoverCard
        openDelay={0}
        closeDelay={100}
        onOpenChange={(open) => {
          if (!open) {
            setIsEditing(false);
            setEditValue(node.attrs.description || '');
          }
        }}
      >
        <HoverCardTrigger>
          <div className={cn('max-w-[100px] cursor-pointer select-none truncate px-1')}>{displayText}</div>
        </HoverCardTrigger>
        <HoverCardPortal container={document.body}>
          <HoverCardContent
            className="flex w-[232px] flex-col border border-system-white08 bg-[rgba(34,37,42,0.64)] px-3 py-2 backdrop-blur-[30px]"
            align="center"
            sideOffset={8}
            side="top"
            style={{
              borderRadius: '12px',
            }}
          >
            <div className="flex items-center justify-between border-system-white08 border-b pb-2 text-sm text-system-white leading-[1.4]">
              {t('agentProject.modelSelect.title')}
              {isEditing ? (
                <Button className="h-5 rounded-full bg-system-white08 px-2 py-0.5" onClick={handleSave}>
                  <BaseLineCheck2 className="size-4 text-system-white" />
                </Button>
              ) : (
                <Button className="h-5 rounded-full bg-system-white08 px-2 py-0.5" onClick={handleEdit}>
                  <BaseLineEdit className="size-4 text-system-white" />
                </Button>
              )}
            </div>
            <Textarea
              className="mt-1 max-h-[300px] flex-1 resize-none border-none bg-transparent p-0 text-sm text-system-white leading-[1.4] placeholder:text-sm placeholder:text-system-white24 focus-visible:ring-0 focus-visible:ring-offset-0"
              value={editValue}
              disabled={!isEditing}
              onChange={(e) => {
                setEditValue(e.target.value);
              }}
            />
          </HoverCardContent>
        </HoverCardPortal>
      </HoverCard>
    </NodeViewWrapper>
  );
};
